/* eslint-disable prettier/prettier */
import React, { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Row, Alert, Col } from 'antd';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import operations from 'apollo/operations';
import {
  Insurance as InsuranceType,
  InsuranceSimulationProduct,
  InsuranceUseType,
  UpdateInsuranceInput,
  CoverageType,
} from 'apollo/types';
import { NextButton } from 'components/ui';
import { DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import downloadPdf from 'utils/downloadPdf';
import Header from 'components/ui/Header';
import InsuranceFilters from 'components/insurance/insuranceFilters';
import InsuranceProducts from 'components/insurance/insuranceProducts';
import { Button } from '@cloudcar-app/cloudcar-components';
import { getRoleFromIdToken } from 'utils/getDataFromJwt';
import styles from './Insurance.module.scss';

interface InsuranceProduct extends InsuranceSimulationProduct {
  selected: boolean;
  disabled: boolean;
  show: boolean;
  coverageDetails: string;
}

const Insurance: FC = (): JSX.Element => {
  const history = useHistory();
  const [voucherPollingActive, setVoucherPollingActive] = useState(true);
  const [enableSaveSimulations, setEnableSaveSimulations] = useState(false);
  const [proceduresInViewMode, setProceduresInViewMode] = useState(false);
  const [enableVoucherDownload, setEnableVoucherDownload] = useState(false);

  const [insuranceProductsOption, setInsuranceProductsOption] = useState<
    InsuranceProduct[]
  >([]);

  const { data: voucherData } = useQuery<{
    insuranceVoucher: string;
  }>(operations.GET_INSURANCE_VOUCHER, {
    fetchPolicy: 'no-cache',
    skip: !enableVoucherDownload,
    pollInterval: voucherPollingActive ? 100000 : 0,
  });

  const [hdiSimulation, { data: hdiSimulationData }] = useLazyQuery<{
    getHdiSimulation: InsuranceSimulationProduct[];
  }>(operations.GET_HDI_SIMULATION, {
    fetchPolicy: 'no-cache',
  });

  const [bciSimulation, { data: bciSimulationData }] = useLazyQuery<{
    getBciSimulation: InsuranceSimulationProduct[];
  }>(operations.GET_BCI_SIMULATION, {
    fetchPolicy: 'no-cache',
  });

  const [zurichSimulation, { data: zurichSimulationData }] = useLazyQuery<{
    getZurichSimulation: InsuranceSimulationProduct[];
  }>(operations.GET_ZURICH_SIMULATION, {
    fetchPolicy: 'no-cache',
  });

  const { data: insuranceData, loading: getInsuranceLoading } = useQuery<{
    getInsurance: InsuranceType;
  }>(operations.GET_INSURANCE, {
    fetchPolicy: 'network-only',
  });

  const [callAnalytics, { loading: analyticsLoading }] = useMutation(
    operations.CALL_ANALYTICS,
  );

  const [
    updateInsurance,
    { data: updateInsuranceData, loading: updateInsuranceLoading },
  ] = useMutation<
    { updateInsurance: InsuranceType },
    { updateInsuranceInput: UpdateInsuranceInput }
  >(operations.UPDATE_INSURANCE);

  const setVoucherPollingActiveFunction = () => {
    if (voucherData && voucherData.insuranceVoucher) {
      setVoucherPollingActive(false);
    }
  };

  const createInsurance = async () => {
    const selectedInsuranceProducts = insuranceProductsOption.filter(
      (option) => {
        return option.selected;
      },
    );

    const finalProducts =
      selectedInsuranceProducts.length > 0
        ? selectedInsuranceProducts
        : insuranceProductsOption;

    if (finalProducts.length > 0) {
      await updateInsurance({
        variables: {
          updateInsuranceInput: {
            insuranceProducts: [
              ...finalProducts.map((option: InsuranceProduct) => {
                const insuranceProduct = {
                  PdfUrl: option.PdfUrl,
                  period: option.period,
                  installments: option.installments,
                  deductible: option.deductible,
                  responseId: option.responseId,
                  productId: option.productId,
                  internalProductId: option.internalProductId,
                  insuranceProvider: option.insuranceProvider,
                  monthlyValue: option.monthlyValue,
                  coverageDetails: option.coverageDetails,
                  monthlyValueInUF: option.monthlyValueInUF,
                  paymentType: option.paymentType,
                  useType: option.useType,
                  coverageType: option.coverageType,
                };
                return insuranceProduct;
              }),
            ],
          },
        },
      });
    }
  };

  const next = async () => {
    if (!proceduresInViewMode) {
      await createInsurance();
    } else {
      history.push({
        pathname: '/',
        state: {
          from: history.location,
        },
      });
    }
  };

  const baseInsuranceProduct = (simulation: InsuranceSimulationProduct) => {
    return {
      ...simulation,
      name: `Deducible ${simulation.deductible}UF`,
      selected: false,
      disabled: false,
      show: true,
    };
  };

  const checkIfProductIsTheSame = (
    option: InsuranceProduct,
    simulation: InsuranceSimulationProduct,
  ) => {
    return (
      option.deductible === simulation.deductible &&
      option.period === simulation.period &&
      option.insuranceProvider === simulation.insuranceProvider &&
      option.coverageType === simulation.coverageType
    );
  };

  const sortInsuranceProducts = (a: InsuranceProduct, b: InsuranceProduct) => {
    const coverageOrder = [
      CoverageType.Light,
      CoverageType.Estandar,
      CoverageType.Full,
      CoverageType.Premium,
      CoverageType.AltaGama,
    ];

    const coverageA = a.coverageType as CoverageType;
    const coverageB = b.coverageType as CoverageType;

    const indexA = coverageOrder.indexOf(coverageA);
    const indexB = coverageOrder.indexOf(coverageB);

    return indexA - indexB;
  };

  const setInsuranceOptions = () => {
    if (
      insuranceData &&
      insuranceData.getInsurance?.selectedInsuranceProducts
    ) {
      const data = insuranceData.getInsurance.selectedInsuranceProducts?.map(
        (insurance) => baseInsuranceProduct(insurance),
      );
      if (data && data.length > 0) {
        setEnableVoucherDownload(true);
        const sortedData = data.sort(sortInsuranceProducts);
        setInsuranceProductsOption(sortedData);
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = [...insuranceProductsOption] as any[];

      if (hdiSimulationData && hdiSimulationData.getHdiSimulation?.length > 0) {
        const hdiProducts = hdiSimulationData.getHdiSimulation.map(
          (simulation) => {
            const productAlreadyExists = insuranceProductsOption.find(
              (option) => checkIfProductIsTheSame(option, simulation),
            );
            if (!productAlreadyExists) {
              return baseInsuranceProduct(simulation);
            }

            return null;
          },
        );
        data.push(...hdiProducts.filter((product) => !!product));
      }
      if (
        zurichSimulationData &&
        zurichSimulationData.getZurichSimulation?.length > 0
      ) {
        const zurichProducts = zurichSimulationData.getZurichSimulation.map(
          (simulation) => {
            const productAlreadyExists = insuranceProductsOption.find(
              (option) => checkIfProductIsTheSame(option, simulation),
            );
            if (!productAlreadyExists) {
              return baseInsuranceProduct(simulation);
            }

            return null;
          },
        );
        data.push(...zurichProducts.filter((product) => !!product));
      }
      if (bciSimulationData && bciSimulationData.getBciSimulation?.length > 0) {
        const bciProducts = bciSimulationData.getBciSimulation.map(
          (simulation) => {
            const productAlreadyExists = insuranceProductsOption.find(
              (option) => checkIfProductIsTheSame(option, simulation),
            );
            if (!productAlreadyExists) {
              return baseInsuranceProduct(simulation);
            }

            if (
              productAlreadyExists &&
              productAlreadyExists.PdfUrl !== simulation.PdfUrl
            ) {
              const indexOfProduct = data.findIndex((option) =>
                checkIfProductIsTheSame(
                  option as InsuranceProduct,
                  productAlreadyExists,
                ),
              );
              if (indexOfProduct > -1) {
                data[indexOfProduct] = {
                  ...productAlreadyExists,
                  PdfUrl: simulation.PdfUrl,
                  productId: simulation.productId,
                  responseId: simulation.responseId,
                };
              }
            }
            return null;
          },
        );

        data.push(...bciProducts.filter((product) => !!product));
      }
      const sortedData = data.sort(sortInsuranceProducts);
      setInsuranceProductsOption(sortedData);
    }
  };

  const [allowedInsurers, setAllowedInsurers] = useState<string[]>([]);

  useEffect(() => {
    setAllowedInsurers(['BCI', 'HDI', 'CHILEAN']);
    
  }, []);

  useEffect(() => {
    if (
      insuranceData &&
      insuranceData.getInsurance &&
      !insuranceData.getInsurance.selectedInsuranceProducts?.length
    ) {
      if (allowedInsurers.length > 0) {
        if (allowedInsurers.includes('BCI')) {
          bciSimulation();
        }
        if (allowedInsurers.includes('HDI')) {
          hdiSimulation();
        }
        if (allowedInsurers.includes('CHILEAN')) {
          zurichSimulation();
        }
      }
    }
  }, [insuranceData, allowedInsurers]);

  useEffect(() => {
    setInsuranceOptions();
  }, [
    insuranceData,
    zurichSimulationData,
    bciSimulationData,
    hdiSimulationData,
  ]);

  useEffect(() => {
    setVoucherPollingActiveFunction();
  }, [voucherData]);

  useEffect(() => {
    if (
      localStorage.getItem('detailsInViewMode') &&
      insuranceData?.getInsurance?.selectedInsuranceProducts &&
      insuranceData?.getInsurance?.selectedInsuranceProducts.length > 0
    ) {
      setProceduresInViewMode(true);
    } else if (
      localStorage.getItem('detailsInViewMode') &&
      !(
        insuranceData?.getInsurance?.selectedInsuranceProducts &&
        insuranceData?.getInsurance?.selectedInsuranceProducts.length > 0
      ) &&
      insuranceProductsOption.length > 0
    ) {
      setProceduresInViewMode(false);
    }
  }, [insuranceData, insuranceProductsOption]);

  useEffect(() => {
    if (updateInsuranceData && updateInsuranceData.updateInsurance) {
      localStorage.setItem('detailsInViewMode', 'true');
      window.location.reload();
    }
  }, [updateInsuranceData]);

  useEffect(() => {
    const enableSaveSimulationsButton = insuranceProductsOption.find(
      (option) => {
        return option.selected;
      },
    );
    setEnableSaveSimulations(!!enableSaveSimulationsButton);
  }, [insuranceProductsOption]);

  const alertHeader = () => (
    <Row style={{ marginBottom: '24px' }}>
      <Alert
        showIcon
        style={{ borderRadius: '4px' }}
        icon={<InfoCircleOutlined />}
        message={
          <>
            {enableVoucherDownload ? (
              <p style={{ fontSize: '12px' }}>
                Los productos se encuentran seleccionados. La descarga de PDF
                puede tardar unos segundos.
              </p>
            ) : (
              <p style={{ fontSize: '12px' }}>
                Recuerda que para guardar los productos y descargar el PDF debes
                seleccionarlos y apretar &quot;Guardar cotizaciones&quot;.
              </p>
            )}
          </>
        }
        type="info"
      />
    </Row>
  );

  const downloadButton = () => {
    return enableVoucherDownload ? (
      <Button
        icon={<DownloadOutlined />}
        disabled={!voucherData?.insuranceVoucher}
        loading={!voucherData?.insuranceVoucher}
        onClick={async () => {
          downloadPdf(
            `${localStorage.getItem('customerRut') || ''}-seguros`,
            voucherData?.insuranceVoucher as string,
          );

          await callAnalytics({
            variables: {
              event: 'evento_seguro_descarga_pdf',
            },
          });
        }}
      >
        Descargar PDF
      </Button>
    ) : (
      <></>
    );
  };

  return (
    <div>
      <Header headerAlert={alertHeader()} downloadButton={downloadButton()} />
      <InsuranceFilters
        proceduresInViewMode={proceduresInViewMode}
        setInsuranceProductsOption={setInsuranceProductsOption}
        insuranceProductsOption={insuranceProductsOption}
        insuranceData={insuranceData}
      />

      <InsuranceProducts
        proceduresInViewMode={proceduresInViewMode}
        setInsuranceProductsOption={setInsuranceProductsOption}
        insuranceProductsOption={insuranceProductsOption}
        zurichSimulationData={zurichSimulationData}
        bciSimulationData={bciSimulationData}
        hdiSimulationData={hdiSimulationData}
        insuranceData={insuranceData}
        allowedInsurers={allowedInsurers}
      />
      <Row className={styles.FixedButtonContainer}>
        <Col xs={24}>
          {enableVoucherDownload &&
          (getRoleFromIdToken() === 'super-admin' ||
            getRoleFromIdToken() === 'usuario-seguros') ? (
            <NextButton
              noMargin
              extraAction={async () => {
                await callAnalytics({
                  variables: {
                    event: 'evento_seguro_emitir_propuesta',
                  },
                });
                history.push({
                  pathname: `/insuranceProposal`,
                  state: {
                    from: history.location,
                  },
                });
              }}
              extraDisabled={analyticsLoading}
              extraText="Emitir propuesta"
              text={
                enableVoucherDownload ? 'Finalizar' : 'Guardar cotizaciones'
              }
              onClick={next}
              disabled={
                (getInsuranceLoading || !enableSaveSimulations) &&
                !enableVoucherDownload
              }
              loading={getInsuranceLoading || updateInsuranceLoading}
            />
          ) : (
            <NextButton
              noMargin
              text={
                enableVoucherDownload ? 'Finalizar' : 'Guardar cotizaciones'
              }
              onClick={next}
              disabled={
                (getInsuranceLoading || !enableSaveSimulations) &&
                !enableVoucherDownload
              }
              loading={getInsuranceLoading || updateInsuranceLoading}
            />
          )}
        </Col>
      </Row>
    </div>
  );
};

export default Insurance;
